package one.healthium.profile.ui.update_initial_weight

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import loco.GENERAL_SAVE
import loco.UPDATE_INITIAL_WEIGHT_SCREEN_TITLE
import loco.UPDATE_TARGET_WEIGHT_SCREEN_TITLE
import one.healthium.profile.ui.update_initial_weight.UpdateInitialWeightEvent.OnInitialWeightUpdated
import one.healthium.profile.ui.weight.WeightPicker
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppProgressButton
import one.healthium.ui.compose.components.AppSheetTopBar
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface UpdateInitialWeightEventDelegate {
    fun onCloseClicked()
    fun onWeightUpdated()
}

@Composable
fun UpdateInitialWeightScreen(
    isTargetWeight: Boolean = false,
    eventDelegate: UpdateInitialWeightEventDelegate
) {
    val viewModel = koinViewModel<UpdateInitialWeightViewModel>()
    val state by viewModel.state(isTargetWeight).collectAsState()

    EventEffect(viewModel.events) { event ->
        when (event) {
            OnInitialWeightUpdated -> eventDelegate.onWeightUpdated()
        }
    }

    UpdateInitialWeightScreenContent(
        state = state,
        onCloseClicked = eventDelegate::onCloseClicked,
        onSaveClicked = viewModel::onSaveClicked,
        onWeightChanged = viewModel::onWeightChanged,
    )

    AlertUiMessageHandler(viewModel.uiMessages)
}

@Composable
private fun UpdateInitialWeightScreenContent(
    state: UpdateInitialWeightUiState,
    onCloseClicked: () -> Unit,
    onSaveClicked: () -> Unit,
    onWeightChanged: (Float) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppSheetTopBar(
            onCloseClicked = onCloseClicked,
        )
        Spacer(Modifier.height(14.dp))
        Text(
            modifier = Modifier.padding(horizontal = 32.dp),
            text = if (state.isTargetWeight) {
                stringResource(loco.Res.string.UPDATE_TARGET_WEIGHT_SCREEN_TITLE)
            } else {
                stringResource(loco.Res.string.UPDATE_INITIAL_WEIGHT_SCREEN_TITLE)
            },
            style = AppTheme.logControlStyle?.titleStyle ?: AppTheme.textStyles.secondary_30_500,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(38.dp))
        if (state.hasInitialWeight) {
            WeightPicker(
                currentWeight = state.weight ?: 0f,
                isMetric = state.isMetric,
                onWeightChanged = { onWeightChanged(it) }
            )
        }
        Spacer(Modifier.height(38.dp))
        AppProgressButton(
            modifier = Modifier.defaultMinSize(minWidth = 176.dp),
            text = stringResource(loco.Res.string.GENERAL_SAVE),
            loading = state.loading,
            onClick = onSaveClicked,
        )
        Spacer(Modifier.height(50.dp))
    }
}
